import { useStore } from "vuex";
import type {
  SiteItem,
  CompanyType,
} from "./types";
import { COMPANY_IDS } from "./types";

export function useDropdownLoaders(companyType: CompanyType) {
  const store = useStore();
  const companyId = companyType === "rental" ? COMPANY_IDS.RENTAL : COMPANY_IDS.FORCE;


  const loadSites = async (): Promise<SiteItem[]> => {
    await store.dispatch('v2/loadSites', companyId);
    const dropdown = store.getters['v2/dropdownSites'] || [];
    return dropdown.map((x: any) => ({ siteProjectId: x.projectId || x.id, siteName: x.name, companyId: x.companyId }));
  };

  const searchSites = async (query: string): Promise<SiteItem[]> => {
    await store.dispatch('v2/searchSites', { query, companyId });
    const dropdown = store.getters['v2/dropdownSites'] || [];
    return dropdown.map((x: any) => ({ siteProjectId: x.projectId || x.id, siteName: x.name, companyId: x.companyId }));
  };


  const loadAssets = async (siteId?: string) => {
    await store.dispatch('v2/loadAssets', { siteId, companyId });
    const dropdown = store.getters['v2/dropdownAssets'] || [];
    return dropdown.map((x: any) => ({
      assetProjectId: x.projectId || x.id,
      assetName: x.name,
      companyId: x.companyId,
      parentProjectId: x.parentProjectId,
    }));
  };

  const searchAssets = async (query: string, siteId?: string) => {
    await store.dispatch('v2/searchAssets', { query, siteId, companyId });
    const dropdown = store.getters['v2/dropdownAssets'] || [];
    return dropdown.map((x: any) => ({
      assetProjectId: x.projectId || x.id,
      assetName: x.name,
      companyId: x.companyId,
      parentProjectId: x.parentProjectId,
    }));
  };


  const loadEquipments = async (siteId?: string) => {
    await store.dispatch('v2/loadEquipments', siteId);
    const dropdown = store.getters['v2/dropdownEquipments'] || [];
    return dropdown.map((x: any) => ({
      assetProjectId: x.projectId || x.id,
      assetName: x.name,
      siteProjectId: x.siteId,
      description: x.description,
    }));
  };

  const searchEquipments = async (query: string, siteId?: string) => {
    await store.dispatch('v2/searchEquipments', { query, siteId });
    const dropdown = store.getters['v2/dropdownEquipments'] || [];
    return dropdown.map((x: any) => ({
      assetProjectId: x.projectId || x.id,
      assetName: x.name,
      siteProjectId: x.siteId,
      description: x.description,
    }));
  };


  const loadWorkOrdersByAsset = async (assetId?: string) => {
    await store.dispatch('v2/loadWorkOrdersByAsset', { assetId, companyId });
    const dropdown = store.getters['v2/dropdownWorkOrders'] || [];
    return dropdown.map((x: any) => ({
      workOrderProjectId: x.projectId || x.id,
      workOrderName: x.name,
      description: x.description,
      companyId: x.companyId,
      parentProjectId: x.parentProjectId,
    }));
  };

  const searchWorkOrdersByAsset = async (query: string, assetId?: string) => {
    await store.dispatch('v2/searchWorkOrdersByAsset', { query, assetId, companyId });
    const dropdown = store.getters['v2/dropdownWorkOrders'] || [];
    return dropdown.map((x: any) => ({
      workOrderProjectId: x.projectId || x.id,
      workOrderName: x.name,
      description: x.description,
      companyId: x.companyId,
      parentProjectId: x.parentProjectId,
    }));
  };


  const loadWorkOrders = async (projectId?: string) => {
    await store.dispatch('v2/loadWorkOrders', projectId);
    const dropdown = store.getters['v2/dropdownWorkOrders'] || [];
    return dropdown.map((x: any) => ({
      workOrderProjectId: x.projectId || x.id,
      workOrderName: x.name,
      description: x.description,
      assetProjectId: x.equipmentId,
    }));
  };

  const searchWorkOrders = async (query: string, projectId?: string) => {
    await store.dispatch('v2/searchWorkOrders', { query, projectId });
    const dropdown = store.getters['v2/dropdownWorkOrders'] || [];
    return dropdown.map((x: any) => ({
      workOrderProjectId: x.projectId || x.id,
      workOrderName: x.name,
      description: x.description,
      assetProjectId: x.equipmentId,
    }));
  };




  const loadOverheadTaskCodes = async () => {
    await store.dispatch('v2/loadOverheadTaskCodes', companyId);
    const dropdown = store.getters['v2/dropdownTasks'] || [];
    return dropdown.map((x: any) => ({
      npCode: x.code,
      npCodeName: x.name,
      companyId: x.companyId,
    }));
  };

  const searchOverheadTaskCodes = async (query: string) => {
    await store.dispatch('v2/searchOverheadTaskCodes', { query, companyId });
    const dropdown = store.getters['v2/dropdownTasks'] || [];
    return dropdown.map((x: any) => ({
      npCode: x.code,
      npCodeName: x.name,
      companyId: x.companyId,
    }));
  };


  const loadActivities = async (projectId?: string) => {
    await store.dispatch('v2/loadActivities', projectId);
    const dropdown = store.getters['v2/dropdownActivities'] || [];
    return dropdown.map((x: any) => ({
      activityId: x.id,
      activityName: x.name,
      description: x.description,
      jobProjectId: x.projectId,
      companyId: x.companyId,
    }));
  };

  const searchActivities = async (query: string, projectId?: string) => {
    await store.dispatch('v2/searchActivities', { query, projectId });
    const dropdown = store.getters['v2/dropdownActivities'] || [];
    return dropdown.map((x: any) => ({
      activityId: x.id,
      activityName: x.name,
      description: x.description,
      jobProjectId: x.projectId,
      companyId: x.companyId,
    }));
  };


  const loadProjects = async (siteId?: string) => {
    await store.dispatch('v2/loadProjects', { siteId });
    const dropdown = store.getters['v2/dropdownProjects'] || [];
    return dropdown.map((x: any) => ({
      jobProjectId: x.projectId || x.id,
      jobName: x.name,
      description: x.description,
      siteProjectId: x.siteId,
    }));
  };

  const searchProjects = async (query: string, siteId?: string) => {
    await store.dispatch('v2/searchProjects', { query, siteId });
    const dropdown = store.getters['v2/dropdownProjects'] || [];
    return dropdown.map((x: any) => ({
      jobProjectId: x.projectId || x.id,
      jobName: x.name,
      description: x.description,
      siteProjectId: x.siteId,
    }));
  };


  const fetchSiteByProjectId = async (projectId: string) => {
    const result = await store.dispatch('v2/fetchSiteByProjectId', projectId);
    if (result) {
      return { siteProjectId: result.projectId || result.id, siteName: result.name, companyId: result.companyId };
    }
    return undefined;
  };

  const fetchProjectById = async (projectId: string) => {
    const result = await store.dispatch('v2/fetchProjectById', projectId);
    if (result) {
      return {
        jobProjectId: result.projectId || result.id,
        jobName: result.name,
        description: result.description,
        siteProjectId: result.siteId,
        companyId: result.companyId
      };
    }
    return undefined;
  };

  const fetchEquipmentByProjectId = async (projectId: string) => {
    const result = await store.dispatch('v2/fetchEquipmentByProjectId', projectId);
    if (result) {
      return { assetProjectId: result.projectId || result.id, assetName: result.name, siteProjectId: result.siteId };
    }
    return undefined;
  };

  return {
    companyId,
    loadSites,
    searchSites,
    // Rental-specific (master API)
    loadAssets,
    searchAssets,
    loadWorkOrdersByAsset,
    searchWorkOrdersByAsset,
    // Overhead Task Codes (for overhead mode)
    loadOverheadTaskCodes,
    searchOverheadTaskCodes,
    // Activities (Force non-overhead)
    loadActivities,
    searchActivities,
    // Legacy
    loadEquipments,
    searchEquipments,
    loadWorkOrders,
    searchWorkOrders,
    loadProjects,
    searchProjects,
    fetchSiteByProjectId,
    fetchProjectById,
    fetchEquipmentByProjectId,
  };
}


const splitProjectId = (pid?: string) => (pid || '').split('.').filter(Boolean);
export const siteProjectFromAny = (pid?: string) => splitProjectId(pid).slice(0, 2).join('.');
export const equipmentProjectFromAny = (pid?: string) => splitProjectId(pid).slice(0, 4).join('.');

